<script setup>
import { onMounted, ref, nextTick } from 'vue'
import axios from 'axios'
import { loadPayline } from '@/payline-helper.js'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'

const route = useRoute()
const router = useRouter()

const produit = ref({})
const prenom = ref(null)
const nom = ref(null)
const email = ref(null)
const erreurMessage = ref(null)
const loading = ref(false)
const paymentToken = ref(undefined)
const isPaiementCollapsed = ref(true)
const showModal = ref(false)
const modalRef = ref(null)
const submitButtonRef = ref(null)

onMounted(() => {
  window.handleFinalStateReached = handleFinalStateReached
  window.handleWidgetDidShowState = handleWidgetDidShowState

  axios
    .get('/produits/' + route.params.codeProduit)
    .then(succes => {
      produit.value = succes.data
      window._paq.push(['addEcommerceItem',
        produit.value.codeProduit,
        produit.value.libelle,
        '',
        produit.value.tarif,
        1
      ])
      window._paq.push(['trackEcommerceCartUpdate', produit.value.tarif])
    }, error => {
      console.error('ERREUR', error.response?.data || error.message)
      router.push({ name: 'erreur' })
    })
})

onBeforeRouteLeave((to) => {
  if (to.name === 'home') {
    window._paq.push(['clearEcommerceCart'])
    window._paq.push(['trackEcommerceCartUpdate'])
  }
})

function toCurrency(value) {
  if (typeof value !== 'number') {
    return value
  }
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(value)
}

function openModal(message) {
  erreurMessage.value = message
  showModal.value = true
  nextTick(() => {
    modalRef.value?.focus()
  })
}

function closeModal() {
  showModal.value = false
  submitButtonRef.value?.focus()
}

function validationFormulaire(e) {
  e.preventDefault()
  if (!e.target.checkValidity()) {
    e.target.reportValidity()
    return
  }
  if (loading.value) {
    return
  }
  loading.value = true

  axios.post('/commandes/prepareVente', {
    codeProduit: produit.value.codeProduit,
    email: email.value,
    prenom: prenom.value,
    nom: nom.value,
    needFacture: false,
    support: 'QRCODE'
  })
    .then(
      succes => {
        paymentToken.value = succes.data.token
        loadPayline(succes.data.production).then(() => {
          isPaiementCollapsed.value = false
        })
      },
      erreur => {
        const msg = erreur?.response?.data?.status === 500
          ? 'Erreur lors de la préparation de la commande.'
          : erreur?.response?.data || 'Erreur lors de la préparation de la commande.'
        openModal(msg)
      }
    )
    .finally(() => {
      loading.value = false
    })
}

function recommencerAchat() {
  window.location.reload()
}

function handleModalKeydown(e) {
  if (e.key === 'Escape') {
    closeModal()
    return
  }
  if (e.key === 'Tab') {
    const focusables = modalRef.value.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }
}

function handleFinalStateReached(param) {
  if (!param) return true
  switch (param.state) {
    case 'PAYMENT_SUCCESS':
    case 'PAYMENT_SUCCESS_FORCE_TICKET_DISPLAY':
    case 'PAYMENT_ONHOLD_PARTNER':
      return true
    default:
      openModal('Echec lors du paiement')
      return false
  }
}

function handleWidgetDidShowState(param) {
  switch (param.state) {
    case 'PAYMENT_METHODS_LIST':
      break
    default:
      erreurMessage.value = null
      break
  }
}
</script>
<template>
  <main>
    <!-- FORMULAIRE -->
    <section
      :class="{ collapse: !isPaiementCollapsed, show: isPaiementCollapsed }"
      :aria-hidden="!isPaiementCollapsed"
      aria-labelledby="titre-achat"
    >
      <form @submit="validationFormulaire" :aria-busy="loading" novalidate>
        <div class="achat container">
          <h1 id="titre-achat">Achat d'un {{ produit.libelle }}</h1>
          <hr/>

          <div class="card form_fabrication_carte">
            <div class="carte_fabrication_carte card">
              <div class="card-body">

                <fieldset>
                  <legend class="visually-hidden">Informations du titulaire</legend>

                  <p id="titulaire-hint">Indiquez les informations du titulaire du ticket</p>

                  <div class="div_icon_input row">
                    <div class="col-sm-1 text-center d-none d-sm-block" aria-hidden="true">
                      <i class="fas fa-user fa-2x"></i>
                    </div>
                    <div class="col-sm-11">
                      <label for="nom">Nom</label>
                      <input
                        v-model="nom"
                        id="nom"
                        type="text"
                        class="form-control"
                        autocomplete="family-name"
                        aria-describedby="titulaire-hint"
                        required
                      />
                    </div>
                  </div>

                  <div class="div_icon_input row">
                    <div class="col-sm-11 offset-sm-1">
                      <label for="prenom">Prénom</label>
                      <input
                        v-model="prenom"
                        id="prenom"
                        type="text"
                        class="form-control"
                        autocomplete="given-name"
                        required
                      />
                    </div>
                  </div>
                </fieldset>

                <fieldset>
                  <legend class="visually-hidden">Coordonnées</legend>

                  <p id="email-hint">
                    Afin de pouvoir valider votre achat, merci de renseigner votre adresse email :
                  </p>

                  <div class="div_icon_input row">
                    <div class="col-sm-1 text-center d-none d-sm-block" aria-hidden="true">
                      <i class="fas fa-envelope fa-2x"></i>
                    </div>
                    <div class="col-sm-11">
                      <label for="email">Adresse e-mail</label>
                      <input
                        v-model="email"
                        id="email"
                        type="email"
                        class="form-control"
                        autocomplete="email"
                        aria-describedby="email-hint"
                        required
                      />
                    </div>
                  </div>
                </fieldset>

                <button
                  ref="submitButtonRef"
                  class="bouton_valid btn btn-lg"
                  type="submit"
                  :disabled="loading"
                >
                  <span v-if="loading" aria-live="polite">Chargement...</span>
                  <span v-else>
                    Valider
                    <i class="fas fa-arrow-right" aria-hidden="true"></i>
                  </span>
                </button>

              </div>
            </div>
          </div>
        </div>
      </form>
    </section>

    <!-- PAIEMENT -->
    <section
      :class="{ collapse: isPaiementCollapsed, show: !isPaiementCollapsed }"
      :aria-hidden="isPaiementCollapsed"
      aria-labelledby="titre-paiement"
    >
      <div class="paiement container">
        <h1 id="titre-paiement">Finalisation de votre achat - Paiement</h1>
        <hr/>

        <div class="carte_recap card">
          <div class="card-body">
            <h2 id="recap-titre" class="h6 card-title">Récapitulatif de votre achat</h2>
            <p class="card-text">
              Titulaire : {{ prenom }} {{ nom }}<br>
              Produit : {{ produit.libelle }}<br>
              Montant : {{ toCurrency(produit.tarif) }}
            </p>
          </div>
        </div>

        <div class="carte_recap card">
          <div class="card-body">
            <div
              id="PaylineWidget"
              :data-token="paymentToken"
              data-template="column"
              data-auto-init="true"
              data-embeddedredirectionallowed="false"
              data-event-didshowstate="handleWidgetDidShowState"
              data-event-finalstatehasbeenreached="handleFinalStateReached"
              role="region"
              aria-label="Module de paiement sécurisé"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- MODALE ERREUR -->
    <div
      v-if="showModal"
      ref="modalRef"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-erreur-titre"
      aria-describedby="modal-erreur-desc"
      style="background: rgba(0,0,0,0.5)"
      @keydown="handleModalKeydown"
    >
      <div class="modal-dialog">
        <div class="modal-content">

          <div class="modal-header">
            <h2 id="modal-erreur-titre" class="modal-title h5">Erreur</h2>
            <button
              type="button"
              class="btn-close"
              aria-label="Fermer la fenêtre"
              @click="closeModal"
            ></button>
          </div>

          <div class="modal-body">
            <p id="modal-erreur-desc" class="description error" role="alert" aria-live="assertive">
              {{ erreurMessage }}
            </p>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-primary"
              @click="recommencerAchat"
            >
              Recommencer
            </button>
          </div>

        </div>
      </div>
    </div>
  </main>
</template>


<style scoped lang="scss">
@import '@/assets/main';

.form_fabrication_carte {
  .carte_fabrication_carte {
    border: none !important;

    .bouton_valid {
      display: block;
      margin: auto;
      @include boutons;
    }

    .bouton_valid:hover {
      @include boutons_hover;
    }

    .bouton_valid:focus-visible {
      outline: 3px solid #212529;
      outline-offset: 2px;
    }

    .div_icon_input {
      display: flex;
      margin-bottom: 1rem;
      @include icon_form;

      &:focus-within i {
        color: $firstcolor;
      }
    }
  }
}

.paiement {
  .carte_recap {
    @include card_shadow;

    p {
      text-align: left;
    }
  }
}

.error {
  color: #b91c1c !important;
}

h1, h2 {
  text-align: center;
}
</style>
